// i18n.ts — the overlay's language layer.
//
// Hannibalbet (Tunisia) offers EN / FR / AR. English is just one of the three, and
// the real audience runs French and Arabic — advice a user can't read is worth
// nothing. So every user-visible string in the SDK resolves through `t()`, and the
// locale is DETECTED from the sportsbook the user is actually looking at, never
// inferred from the operator's geo (a Tunisian book can be, and was, running RU).
//
// DESIGN RULES
//  - Internal keys stay English. `momentum.kind`, `pick_state`, the backend's band
//    strings etc. are compared as values in logic — translating those breaks the
//    logic silently. We translate at the render edge only.
//  - No fragment-gluing. FR and AR reorder, so a message is a whole sentence with
//    named params, never `partA + " · " + partB` assembled by the caller.
//  - Params are bidi-isolated in RTL. Team names, scores and odds are LTR runs
//    inside an Arabic sentence; without isolates "2 – 0" visually renders "0 – 2".
//  - Default locale is "en" and a missing key falls back to English, so an
//    untranslated string is always readable rather than blank.

// en/fr/ar — Hannibalbet (Tunisia). ru/tk — Parikara (Turkmenistan), whose switcher
// offers Türkmen / Русский / English and DEFAULTS to Russian. Both new locales are
// LTR, so none of the RTL machinery below applies to them.
export type Locale = "en" | "fr" | "ar" | "ru" | "tk";

// All five ship. Every non-English catalog is built from the operator's OWN sportsbook
// vocabulary (research/lexicon/), not by translating the English.
//
// `tk` is the exception worth knowing: Digitain has NO Turkmen — it renders the board in
// TURKISH for a Türkmen user. So Turkmen is built from Turkmen sports press + Parikara's
// own Turkmen shell. Grounded in published Turkmen, but not natively reviewed.
export const SUPPORTED_LOCALES: Locale[] = ["en", "fr", "ar", "ru", "tk"];
export const DEFAULT_LOCALE: Locale = "en";

/** Locales that read right-to-left. */
export function isRTL(locale: Locale): boolean {
  return locale === "ar";
}

// Unicode bidi isolates (FSI … PDI). Wrapping a Latin/numeric run in these tells
// the bidi algorithm to lay it out independently of the surrounding Arabic, which
// is what keeps "2 – 0" and "2.1 → 1.85" reading in the right order.
const FSI = "⁨";
const PDI = "⁩";

// Arabic/Hebrew ranges — a value containing these is already RTL prose, not an
// LTR run, so isolating it would be wrong (and would nest isolates when a
// translated fragment is interpolated into another translated message).
const RTL_CHARS = /[֐-׿؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/;
// Something the bidi algorithm would lay out left-to-right: a Latin letter or a digit.
const LTR_CHARS = /[A-Za-z0-9À-ɏ]/;

/**
 * Isolate a directional run (odds, score, team name) inside RTL text.
 *
 * Only genuine LTR runs are wrapped. Isolating already-Arabic text is pointless
 * and produces nested isolates when one translated message is interpolated into
 * another; isolating a bare emoji is noise. No-op in LTR, so English output — and
 * every existing test assertion — is untouched.
 */
export function ltr(value: string | number, locale: Locale = DEFAULT_LOCALE): string {
  const s = String(value);
  if (!isRTL(locale) || s === "") return s;
  if (s.startsWith(FSI)) return s;            // already isolated — don't nest
  if (RTL_CHARS.test(s)) return s;            // RTL prose, not an LTR run
  if (!LTR_CHARS.test(s)) return s;           // no directional content (emoji, punctuation)
  return FSI + s + PDI;
}

/**
 * A percentage as ONE token — "62%", "~62%".
 *
 * The `%` and a leading `~` are bidi-NEUTRAL characters. Left in the message
 * template they sit outside the param's isolate, and Arabic pushes them to the far
 * side of the number: "62%" renders as "%62". So the sign travels WITH the digits
 * and the whole token gets isolated as one run. Same reason `oddsMove` exists.
 */
export function fmtPct(pct: number, approx = false): string {
  return `${approx ? "~" : ""}${pct}%`;
}

/** An odds movement as ONE token — "2.9→2.45". The arrow is a neutral too, so
 * isolating the two prices separately would let it drift and invert the read. */
export function oddsMove(from: string | number, to: string | number): string {
  return `${from}→${to}`;
}

/** Map whatever the sportsbook reports ("EN", "fr-FR", "ar", 2, …) onto a locale
 * we actually support. Unrecognised → null, so the caller can try the next signal
 * in the chain rather than silently claiming English. */
export function normalizeLocale(raw: unknown): Locale | null {
  if (raw == null) return null;
  const s = String(raw).trim().toLowerCase();
  if (!s) return null;
  // "fr-FR" / "fr_FR" / "ar-TN" → primary subtag.
  const base = s.split(/[-_]/)[0];
  if ((SUPPORTED_LOCALES as string[]).indexOf(base) >= 0) return base as Locale;
  return null;
}

// The operator's language switcher, in the PARENT page — each operator skins it
// differently, so this is a LIST and the first one present wins.
//   Hannibalbet: renders its current choice as "EN" / "FR" / "AR".
//   Parikara:    renders it as "ru" / "tk" / "en" (verified live 2026-07-13).
const LANG_SELECTORS = [
  ".language-dropdown-selector-desktop__current-name",
  ".set-language__base",
];

/** The language the user picked, read from the page they're looking at.
 *
 * First hit wins. The chain is ordered by how directly each signal reflects the
 * USER's choice, and it degrades safely: if the switcher turns out to live inside
 * the cross-origin sportsbook iframe (unreadable from here), `setts.language` —
 * which the operator's own page hands to Digitain's bootstrapper — carries it.
 *
 * `<html lang>` is LAST on purpose and is deliberately not trusted over the two
 * above it: Parikara sets `lang="tr"` (TURKISH) when the user picks Türkmen
 * (verified live). Their switcher reports the correct `tk`, so the switcher must
 * win — trusting the document would mislabel every Turkmen user. */
export function detectLocale(fallback?: unknown): Locale {
  const signals: unknown[] = [];
  for (const sel of LANG_SELECTORS) {
    try {
      const el = document.querySelector(sel);
      if (el) signals.push(el.textContent);
    } catch {
      /* no document */
    }
  }
  try {
    signals.push((window as unknown as { SportFrame?: { setts?: Record<string, unknown> } })
      .SportFrame?.setts?.language);
  } catch {
    /* SportFrame not booted */
  }
  try {
    signals.push(document.documentElement.getAttribute("lang"));
  } catch {
    /* no document */
  }
  signals.push(fallback);

  for (const sig of signals) {
    const loc = normalizeLocale(sig);
    if (loc) return loc;
  }
  return DEFAULT_LOCALE;
}

/** Poll for a language switch and fire on change. The sportsbook is a SPA — the
 * user can switch language without a reload, and an overlay still speaking French
 * on an Arabic page reads as broken. Returns a stop function.
 *
 * A poll rather than a MutationObserver: the switcher may not be in our DOM at all
 * (cross-origin iframe), in which case only `setts.language` moves, and nothing
 * observable mutates. 2s is far below human switch-and-look latency and the read
 * is two property lookups. */
export function watchLocale(
  current: Locale,
  onChange: (next: Locale) => void,
  fallback?: unknown,
  intervalMs = 2000,
): () => void {
  let last = current;
  const timer = setInterval(() => {
    const next = detectLocale(fallback);
    if (next !== last) {
      last = next;
      onChange(next);
    }
  }, intervalMs);
  return () => clearInterval(timer);
}

// ── Catalog ───────────────────────────────────────────────────────────────────

/** A message is either one string, or CLDR plural forms selected by `{n}`. */
type Message = string | Partial<Record<Intl.LDMLPluralRule, string>>;
type Catalog = Record<string, Message>;

const EN: Catalog = {

  // Launcher.
  "launcher.eyebrow.live_read": "LIVE READ",
  "launcher.eyebrow.live_now": "LIVE NOW",
  "launcher.eyebrow.live_picks": "LIVE PICKS",
  "launcher.eyebrow.ai": "AI TIPS",
  "launcher.eyebrow.slip": "IN YOUR BETSLIP",
  "slip.nudge": "Your pick is still in the betslip — price {odds}",
  "slip.nudge_plain": "Your pick is still in the betslip",
  "launcher.recent_play": "{teams}: {chip}",
  "launcher.fixture": "{teams}",
  "launcher.fixture_minute": "{teams}, {minute}'",
  "launcher.fixture_starts": "{teams} \u00B7\u00A0{starts}",
  "launcher.fixture_starts_wrapped": "{teams}\n{starts}",
  "time.minutes_short": "{n}\u00A0min",
  "time.hours_short": "{n}\u00A0h",
  "launcher.fixture_score": "{teams} ·\u00A0{score}",
  // The WRAPPED form of the same line, chosen by measurement when the one-line
  // form will not fit the chip. It carries a newline where the one-line form
  // carries the separator: a "·" left leading the second line points at nothing
  // (owner, 2026-08-05). A full template per locale, never the one above with a
  // fragment swapped in — see the assembly rule at the top of this file.
  "launcher.fixture_score_wrapped": "{teams}\n{score}",
  "launcher.compact": "Live picks",
  "launcher.coupon_more": {
    one: "{n} more live favorite",
    other: "{n} more live favorites",
  },
  "peek.position": "{n}/{total}",
  "peek.all_picks": "All picks",
  "nps.question": "How much do you like our tips?",
  "nps.low": "Not at all",
  "nps.high": "Love them",
  "nps.thanks": "Thank you!",
  "peek.skip": "Skip",
  "peek.prev": "Previous pick",
  "peek.next": "Next pick",
  "peek.goto": "Pick {n}",

  // Panel chrome.
  "panel.title": "Live picks for you",
  "panel.warming": "warming up…",
  "panel.finding_value": "Finding live value…",
  "panel.collapse": "Collapse",
  "panel.hide": "Hide",
  "panel.subtitle": "{picks} · {when}",
  "panel.picks": { one: "{n} live pick", other: "{n} live picks" },
  "panel.updated_now": "updated now",
  "panel.updated_min": { one: "updated {n}m ago", other: "updated {n}m ago" },
  "panel.updated_sec": { one: "updated {n}s ago", other: "updated {n}s ago" },

  // Chips.
  "chip.live": "LIVE",
  // P4 (owner 2026-10-08): players who bet on THIS MATCH in 24h — never "this pick".
  "chip.bettors": { one: "{n} player bet on this match", other: "{n} players bet on this match" },
  "chip.live_count": "{n} LIVE",
  "chip.tv": "Live on TV",
  "chip.tv_all": "All on TV",
  "chip.multi_sport": "Multi-sport",

  // Combo card. NOTE `pct` arrives pre-formatted ("62%" / "~62%") — see fmtPct.
  //
  // TWO subtitles, and which one the card gets is a claim about the legs, not a
  // style choice. `combo.legcount` says FAVORITES and may only be used when every
  // leg really is one (odds <= FAVORITE_MAX_ODDS, i.e. the book makes that side
  // more likely than not). It used to be unconditional: measured on Parikara
  // 2026-08-06, an acca carrying a 2.16 leg read «3 фаворита в лайве» directly
  // above its own «вероятность ~44%» — the card contradicting itself in two
  // adjacent lines. Anything short of all-favorites gets the neutral count.
  "combo.selection": { one: "{n}-fold acca", other: "{n}-fold acca" },
  "combo.legcount": { one: "{n} live favorite", other: "{n} live favorites" },
  "combo.legcount_live": { one: "{n} live event", other: "{n} live events" },
  // Pre-match variants: a same-match express on a fixture hours out is NOT live,
  // so the subtitle drops the "live" word. RU and TK carry their own (TK added
  // 2026-10-15 after a Turkmen card read «3 events»); FR/AR fall back to EN.
  "combo.legcount_pre": { one: "{n} favorite", other: "{n} favorites" },
  "combo.legcount_pre_live": { one: "{n} event", other: "{n} events" },
  "combo.implied_approx": "{pct} implied",
  "combo.tag_world_cup": "World Cup Acca",
  "combo.tag_focus": "{league} Acca",
  "combo.tag_live": "Live Acca",
  "combo.tag_pre": "Acca",

  // Single card + meter.
  "card.vs": "{home} vs {away}",

  // "One to watch" card (no priced favorite).
  "watch.eyebrow": "◆ One to watch",
  "watch.live_match": "Live match",
  "watch.suspended": "Line temporarily suspended — no favorite priced right now.",

  // Call to action.
  "cta.add": "Add to betslip",
  "cta.add_acca": "Add acca to betslip",
  // Bet-builder same-match combo — this PLACES a real bet, so the button names
  // the action plainly. EN-only: RU/FR/AR fall back to EN (English is parseable;
  // an invented imperative is not — hard rule).
  "cta.place_bet": "Place bet",
  "cta.stake": "Stake",
  "cta.cancel": "Cancel",
  "cta.adding": "Adding…",
  "cta.sent": "✓ Sent to betslip",
  "cta.sent_acca": "Sent — check acca legs",
  "cta.in_slip": "✓ In your betslip",
  "cta.open_slip": "Open betslip",
  "cta.opened": "Opened in sportsbook",
  // SoftSwiss failed-add: nothing was clicked and nothing navigates on that path,
  // so "Opened in sportsbook" would be a lie there. (EN-only demo path — other
  // locales fall back to EN.)
  "cta.add_failed": "Couldn't add — find it in the sportsbook",
  // «Open match», the card's primary action. ⛔ Deliberately absent
  // from the FR and AR catalogs: t() falls back to EN for a missing key, and
  // English is a real language a reader can parse — an invented French or Arabic
  // imperative is not. Fill them in when a native read arrives.
  "cta.view_match": "Open match",
  // ⛔ Names what the button DOES. `dismiss()` (renderer.ts) retires this pick AND
  // advances the peek to the next one — owner override 2026-07-31 — so "Skip"
  // described only the retire half and read as a loss. FR and AR keep their
  // reviewed wording until someone reads a replacement.
  "cta.skip": "Another tip",

  // Match-page tiered lineup (Task 10). RU has a native read; TK/FR/AR fall
  // back to this EN text until a native read arrives (hard rule: never ship
  // prose nobody can read).
  "tier.low": "Low risk",
  "tier.medium": "Medium risk",
  "tier.high": "High risk",
};

const FR: Catalog = {

  "launcher.eyebrow.live_read": "LECTURE LIVE",
  "launcher.eyebrow.live_now": "EN DIRECT",
  "launcher.eyebrow.live_picks": "SÉLECTIONS",
  // ⚠️ UNREVIEWED — no native read. Standard FR for "AI advice"; verify before a FR pilot.
  "launcher.eyebrow.ai": "CONSEILS IA",
  "launcher.eyebrow.slip": "DANS VOTRE COUPON",
  "slip.nudge": "Votre pari est encore dans le coupon — cote {odds}",
  "slip.nudge_plain": "Votre pari est encore dans le coupon",
  "launcher.recent_play": "{teams}: {chip}",
  "launcher.fixture": "{teams}",
  "launcher.fixture_minute": "{teams}, {minute}'",
  "launcher.fixture_starts": "{teams} \u00B7\u00A0{starts}",
  "launcher.fixture_starts_wrapped": "{teams}\n{starts}",
  "time.minutes_short": "{n}\u00A0min",
  "time.hours_short": "{n}\u00A0h",
  "launcher.fixture_score": "{teams} ·\u00A0{score}",
  // The WRAPPED form of the same line, chosen by measurement when the one-line
  // form will not fit the chip. It carries a newline where the one-line form
  // carries the separator: a "·" left leading the second line points at nothing
  // (owner, 2026-08-05). A full template per locale, never the one above with a
  // fragment swapped in — see the assembly rule at the top of this file.
  "launcher.fixture_score_wrapped": "{teams}\n{score}",
  "launcher.compact": "Paris en direct",
  "launcher.coupon_more": {
    one: "{n} favori en direct",
    other: "{n} favoris en direct",
  },
  "peek.position": "{n}/{total}",
  "peek.all_picks": "Tous les paris",
  "peek.skip": "Passer",
  "peek.prev": "Pari précédent",
  "peek.next": "Pari suivant",
  "peek.goto": "Pari {n}",

  "panel.title": "Sélections en direct",
  "panel.warming": "chargement…",
  "panel.finding_value": "Recherche de valeur…",
  "panel.collapse": "Réduire",
  "panel.subtitle": "{picks} · {when}",
  "panel.picks": { one: "{n} sélection en direct", other: "{n} sélections en direct" },
  // Kept short: the subtitle sits on one 10px line next to the pick count, and the
  // full "mis à jour à l'instant" wrapped it to two (seen in the visual harness).
  "panel.updated_now": "à l'instant",
  "panel.updated_min": { one: "il y a {n} min", other: "il y a {n} min" },
  "panel.updated_sec": { one: "il y a {n} s", other: "il y a {n} s" },

  "chip.live": "EN DIRECT",
  "chip.live_count": "{n} EN DIRECT",
  "chip.tv": "En direct à la TV",
  "chip.tv_all": "Tous à la TV",
  "chip.multi_sport": "Multi-sports",

  "combo.selection": { one: "Combiné {n} sélection", other: "Combiné {n} sélections" },
  "combo.legcount": { one: "{n} favori en direct", other: "{n} favoris en direct" },
  // «sélection» is Digitain's own French word for a leg (research/lexicon/fr.md,
  // and every other FR acca string here) — not "événement".
  "combo.legcount_live": { one: "{n} sélection en direct", other: "{n} sélections en direct" },
  "combo.implied_approx": "{pct} implicite",
  "combo.tag_world_cup": "Combiné Coupe du Monde",
  "combo.tag_focus": "Combiné {league}",
  "combo.tag_live": "Combiné en direct",

  "card.vs": "{home} vs {away}",

  "watch.eyebrow": "◆ À surveiller",
  "watch.live_match": "Match en direct",
  "watch.suspended": "Paris suspendus — aucun favori coté pour le moment.",

  "cta.add": "Ajouter au coupon",
  // "Ajouter le combiné au coupon" wrapped the CTA onto two lines (visual harness).
  "cta.add_acca": "Ajouter le combiné",
  "cta.adding": "Ajout…",
  "cta.sent": "✓ Ajouté au coupon",
  "cta.sent_acca": "Ajouté — vérifiez le coupon",
  "cta.in_slip": "✓ Dans votre coupon",
  "cta.open_slip": "Ouvrir le coupon",
  "cta.opened": "Ouvert sur le site",
  // ⚠️ UNREVIEWED by a native reader — RU is the ship-gating locale (mobile design
  // spec §). Mirrors this catalog's own "cta.opened" phrasing rather than inventing one.
  "cta.add_failed": "Ajout impossible — à retrouver sur le site",
  "cta.skip": "Passer",
};

const AR: Catalog = {

  "launcher.eyebrow.live_read": "قراءة مباشرة",
  "launcher.eyebrow.live_now": "مباشر الآن",
  "launcher.eyebrow.live_picks": "اختيارات مباشرة",
  // Deliberately the SAME string as live_read above — reviewed Arabic, reused verbatim.
  // «الذكاء الاصطناعي» is far too long for a 9px uppercase eyebrow, and inventing a
  // shorter Arabic coinage would break the "never ship prose you cannot read" rule.
  "launcher.eyebrow.ai": "قراءة مباشرة",
  "launcher.eyebrow.slip": "في إيصال الرهان",
  "slip.nudge": "اختيارك ما زال في إيصال الرهان — السعر {odds}",
  "slip.nudge_plain": "اختيارك ما زال في إيصال الرهان",
  "launcher.recent_play": "{teams}: {chip}",
  "launcher.fixture": "{teams}",
  "launcher.fixture_minute": "{teams}، {minute}'",
  "launcher.fixture_starts": "{teams} \u00B7\u00A0{starts}",
  "launcher.fixture_starts_wrapped": "{teams}\n{starts}",
  "time.minutes_short": "{n}\u00A0min",
  "time.hours_short": "{n}\u00A0h",
  "launcher.fixture_score": "{teams} ·\u00A0{score}",
  // The WRAPPED form of the same line, chosen by measurement when the one-line
  // form will not fit the chip. It carries a newline where the one-line form
  // carries the separator: a "·" left leading the second line points at nothing
  // (owner, 2026-08-05). A full template per locale, never the one above with a
  // fragment swapped in — see the assembly rule at the top of this file.
  "launcher.fixture_score_wrapped": "{teams}\n{score}",
  "launcher.compact": "رهانات مباشرة",
  "launcher.coupon_more": {
    zero: "{n} مفضل مباشر إضافي",
    one: "مفضل مباشر إضافي",
    two: "مفضلان مباشران إضافيان",
    few: "{n} مفضلين مباشرين إضافيين",
    many: "{n} مفضلاً مباشراً إضافياً",
    other: "{n} مفضل مباشر إضافي",
  },
  "peek.position": "{n}/{total}",
  "peek.all_picks": "كل الرهانات",
  "peek.skip": "تخطٍ",
  "peek.prev": "الرهان السابق",
  "peek.next": "الرهان التالي",
  "peek.goto": "الرهان {n}",

  "panel.title": "اختيارات مباشرة لك",
  "panel.warming": "جارٍ التحميل…",
  "panel.finding_value": "البحث عن فرص مباشرة…",
  "panel.collapse": "طيّ",
  "panel.subtitle": "{picks} · {when}",
  "panel.picks": {
    zero: "لا اختيارات مباشرة",
    one: "اختيار مباشر واحد",
    two: "اختياران مباشران",
    few: "{n} اختيارات مباشرة",
    many: "{n} اختيارًا مباشرًا",
    other: "{n} اختيار مباشر",
  },
  "panel.updated_now": "تم التحديث الآن",
  "panel.updated_min": {
    one: "قبل دقيقة",
    two: "قبل دقيقتين",
    few: "قبل {n} دقائق",
    many: "قبل {n} دقيقة",
    other: "قبل {n} دقيقة",
  },
  "panel.updated_sec": {
    one: "قبل ثانية",
    two: "قبل ثانيتين",
    few: "قبل {n} ثوانٍ",
    many: "قبل {n} ثانية",
    other: "قبل {n} ثانية",
  },

  "chip.live": "مباشر",
  "chip.live_count": "{n} مباشر",
  "chip.tv": "تليفزيون مباشر",
  "chip.tv_all": "الكل على التليفزيون",
  "chip.multi_sport": "رياضات متعددة",

  "combo.selection": { one: "مجمّع من اختيار واحد", two: "مجمّع من اختيارين", few: "مجمّع من {n} اختيارات", many: "مجمّع من {n} اختيارًا", other: "مجمّع من {n} اختيار" },
  "combo.legcount": {
    zero: "لا مرشحين مباشرين",
    one: "مرشح مباشر واحد",
    two: "مرشحان مباشران",
    few: "{n} مرشحين مباشرين",
    many: "{n} مرشحًا مباشرًا",
    other: "{n} مرشح مباشر",
  },
  // Built by taking the reviewed AR acca strings (`combo.selection` in the
  // backend catalog, «مجمّع مباشر من {n} اختيار») and keeping the leg noun +
  // «مباشر», in the same six forms. Reassembled from reviewed copy, not
  // translated from the English — still owed one native read.
  "combo.legcount_live": {
    zero: "لا اختيارات مباشرة",
    one: "اختيار مباشر واحد",
    two: "اختياران مباشران",
    few: "{n} اختيارات مباشرة",
    many: "{n} اختيارًا مباشرًا",
    other: "{n} اختيار مباشر",
  },
  "combo.implied_approx": "{pct} احتمال ضمني",
  "combo.tag_world_cup": "مجمّع كأس العالم",
  "combo.tag_focus": "مجمّع {league}",
  "combo.tag_live": "مجمّع مباشر",

  "card.vs": "{home} ضد {away}",

  "watch.eyebrow": "◆ جديرة بالمتابعة",
  "watch.live_match": "مباراة مباشرة",
  "watch.suspended": "الأسعار متوقفة مؤقتًا — لا يوجد مرشح مسعّر حاليًا.",

  "cta.add": "أضف إلى إيصال الرهان",
  "cta.add_acca": "أضف المجمّع إلى إيصال الرهان",
  "cta.adding": "جارٍ الإضافة…",
  "cta.sent": "✓ أُضيف إلى إيصال الرهان",
  "cta.sent_acca": "أُضيف — تحقق من إيصال الرهان",
  "cta.in_slip": "✓ في إيصال الرهان",
  "cta.open_slip": "فتح إيصال الرهان",
  "cta.opened": "فُتح في الموقع",
  // ⚠️ UNREVIEWED by a native reader — RU is the ship-gating locale (mobile design
  // spec §). Mirrors this catalog's own "cta.opened" phrasing rather than inventing one.
  "cta.add_failed": "تعذّرت الإضافة — ابحث عنه في الموقع",
  "cta.skip": "تخطٍّ",
};

// Russian — Parikara's DEFAULT language (Turkmenistan). LTR.
// Plurals are the real work here: RU has one/few/many/other
// (1 ставка · 2–4 ставки · 5–20 ставок), which Intl.PluralRules selects for us.
const RU: Catalog = {
  // Russian — Parikara's DEFAULT language. Written in the register a Russian
  // bookmaker actually uses (коэффициент, линия, исход, купон, экспресс), NOT
  // translated word-for-word from the English. Plurals: one/few/many.

  "launcher.eyebrow.live_read": "СИГНАЛ",
  "launcher.eyebrow.live_now": "ИДЁТ МАТЧ",
  "launcher.eyebrow.live_picks": "СТАВКИ В ЛАЙВЕ",
  // Owner-chosen from the launcher lab, 2026-08-03 (name=C, reconfirmed at eyebrow2=A).
  "launcher.eyebrow.ai": "AI СОВЕТЫ",
  "launcher.eyebrow.slip": "В ВАШЕМ БИЛЕТЕ",
  "slip.nudge": "Ваша ставка ещё в билете — коэффициент {odds}",
  "slip.nudge_plain": "Ваша ставка ещё в билете",
  "launcher.recent_play": "{teams}: {chip}",
  "launcher.fixture": "{teams}",
  "launcher.fixture_minute": "{teams}, {minute}'",
  "launcher.fixture_starts": "{teams} \u00B7\u00A0\u0447\u0435\u0440\u0435\u0437\u00A0{starts}",
  "launcher.fixture_starts_wrapped": "{teams}\n\u0447\u0435\u0440\u0435\u0437\u00A0{starts}",
  "time.minutes_short": "{n}\u00A0\u043C\u0438\u043D",
  "time.hours_short": "{n}\u00A0\u0447",
  "launcher.fixture_score": "{teams} ·\u00A0{score}",
  // The WRAPPED form of the same line, chosen by measurement when the one-line
  // form will not fit the chip. It carries a newline where the one-line form
  // carries the separator: a "·" left leading the second line points at nothing
  // (owner, 2026-08-05). A full template per locale, never the one above with a
  // fragment swapped in — see the assembly rule at the top of this file.
  "launcher.fixture_score_wrapped": "{teams}\n{score}",
  "launcher.compact": "Ставки в лайве",
  "launcher.coupon_more": {
    one: "Ещё {n} фаворит в лайве",
    few: "Ещё {n} фаворита в лайве",
    many: "Ещё {n} фаворитов в лайве",
    other: "Ещё {n} фаворитов в лайве",
  },
  "peek.position": "{n}/{total}",
  "peek.all_picks": "Все ставки",
  "nps.question": "Насколько вам нравятся наши советы?",
  "nps.low": "Совсем нет",
  "nps.high": "Очень",
  "nps.thanks": "Спасибо!",
  "peek.skip": "Пропустить",
  "peek.prev": "Предыдущая ставка",
  "peek.next": "Следующая ставка",
  "peek.goto": "Ставка {n}",

  "panel.title": "Ставки в лайве",
  "panel.warming": "загрузка…",
  "panel.finding_value": "Ищем ставки…",
  "panel.collapse": "Свернуть",
  "panel.hide": "Скрыть",
  "panel.subtitle": "{picks} · {when}",
  "panel.picks": {
    one: "{n} ставка в лайве",
    few: "{n} ставки в лайве",
    many: "{n} ставок в лайве",
    other: "{n} ставок в лайве",
  },
  "panel.updated_now": "обновлено сейчас",
  "panel.updated_min": { one: "{n} мин назад", few: "{n} мин назад",
                         many: "{n} мин назад", other: "{n} мин назад" },
  "panel.updated_sec": { one: "{n} сек назад", few: "{n} сек назад",
                         many: "{n} сек назад", other: "{n} сек назад" },

  "chip.live": "ЛАЙВ",
  // P4: «поставил 21 игрок / поставили 22 игрока / поставили 25 игроков».
  "chip.bettors": {
    one: "На этот матч поставил {n} игрок",
    few: "На этот матч поставили {n} игрока",
    many: "На этот матч поставили {n} игроков",
    other: "На этот матч поставили {n} игрока",
  },
  "chip.live_count": "{n} В ЛАЙВЕ",
  "chip.tv": "ТВ",
  "chip.tv_all": "Все по ТВ",
  "chip.multi_sport": "Разные виды спорта",

  "combo.selection": { one: "Экспресс из {n} события", few: "Экспресс из {n} событий", many: "Экспресс из {n} событий", other: "Экспресс из {n} событий" },
  "combo.legcount": {
    one: "{n} фаворит в лайве",
    few: "{n} фаворита в лайве",
    many: "{n} фаворитов в лайве",
    other: "{n} фаворитов в лайве",
  },
  // «событие в лайве» is the backend catalog's own reviewed acca wording
  // («Экспресс из {n} событий в лайве»), with the leading clause dropped.
  "combo.legcount_live": {
    one: "{n} событие в лайве",
    few: "{n} события в лайве",
    many: "{n} событий в лайве",
    other: "{n} событий в лайве",
  },
  "combo.implied_approx": "вероятность {pct}",
  "combo.tag_world_cup": "Экспресс на ЧМ",
  "combo.tag_focus": "Экспресс: {league}",
  "combo.tag_live": "Лайв-экспресс",
  // Pre-match variants (drop «в лайве»; reuses the operator's own reviewed wording).
  "combo.tag_pre": "Экспресс",
  "combo.legcount_pre": {
    one: "{n} фаворит",
    few: "{n} фаворита",
    many: "{n} фаворитов",
    other: "{n} фаворитов",
  },
  "combo.legcount_pre_live": {
    one: "{n} событие",
    few: "{n} события",
    many: "{n} событий",
    other: "{n} событий",
  },

  "card.vs": "{home} — {away}",

  "watch.eyebrow": "◆ Стоит посмотреть",
  "watch.live_match": "Матч в лайве",
  "watch.suspended": "Ставки приостановлены — коэффициента на фаворита сейчас нет.",

  "cta.add": "Добавить в билет",
  "cta.add_acca": "Добавить экспресс",
  "cta.adding": "Добавляем…",
  "cta.sent": "✓ Добавлено в билет",
  "cta.sent_acca": "Отправлено — проверьте билет",
  "cta.in_slip": "✓ В вашем билете",
  "cta.open_slip": "Открыть билет",
  "cta.opened": "Открыто у букмекера",
  // Deliberately mirrors "Открыто у букмекера" above: same «у букмекера» ending, so
  // the two failure-adjacent states read as one voice. Avoids «в линии» — that word
  // excludes лайв, and this fires on live picks too.
  "cta.add_failed": "Не удалось добавить — найдите у букмекера",
  // Owner native read 2026-09-01. Digitain renders no "open the match" label of
  // its own anywhere in its Russian UI — see docs/lexicon/ru.md §4b, which
  // records the search — so this is written, not harvested.
  "cta.view_match": "Открыть матч",
  "cta.skip": "Другой совет",

  // Lexicon ru.md («СДЕЛАТЬ СТАВКУ»); Codex prose review PASS 2026-10-15.
  "cta.place_bet": "Сделать ставку",
  "cta.stake": "Сумма ставки",
  "cta.cancel": "Отмена",
  "tier.low": "Низкий риск",
  "tier.medium": "Средний риск",
  "tier.high": "Высокий риск",
};

// Turkmen — Parikara's national language. LTR, Latin script, one/other plurals.

// Turkmen — Parikara's national language. LTR, Latin script, one/other plurals.
// NOTE their site sets <html lang="tr"> (Turkish) for Turkmen; the switcher reports
// the correct "tk", which is why detectLocale prefers the switcher (see above).
const TK: Catalog = {
  // Turkmen — built from Turkmen sports press + Parikara's own Turkmen shell, NOT from
  // Digitain (which has no Turkmen and serves TURKISH instead). See research/lexicon/tk.md.
  //   goýum = bet · koeffisiýent = odds · duşuşyk = match · hasap = score ·
  //   ýeňiş = win · utulmak = to lose · deňme-deň = draw · esasy dalaşgär = the favourite
  // NEVER use the Turkish words (bahis, oran, maç) — they read as foreign in Turkmen.

  "launcher.eyebrow.live_read": "SIGNAL",
  "launcher.eyebrow.live_now": "DUŞUŞYK DOWAM EDÝÄR",
  "launcher.eyebrow.live_picks": "GÖNI GOÝUMLAR",
  // Same as live_read above — reused verbatim rather than coining Turkmen for "AI".
  "launcher.eyebrow.ai": "SIGNAL",
  "launcher.eyebrow.slip": "PETEGIŇIZDE",
  "slip.nudge": "Saýlawyňyz entek petegiňizde — koeffisiýent {odds}",
  "slip.nudge_plain": "Saýlawyňyz entek petegiňizde",
  "launcher.recent_play": "{teams}: {chip}",
  "launcher.fixture": "{teams}",
  "launcher.fixture_minute": "{teams}, {minute}'",
  // ⚠️ TURKMEN IS A LIVE PILOT LOCALE (Parikara serves Türkmen / Русский /
  // English), so these two go to real users the moment the pre-match flag turns
  // on — unlike fr/ar, which are Hannibalbet's.
  //
  // `sag` is the Turkmen abbreviation of «sagat» (hour); `min` matches this same
  // catalog's own published-Turkmen convention («{n} min öň» in panel.updated_min).
  // ⛔ The template stays the NEUTRAL separator form with no preposition, and that
  // is deliberate: "in 6 hours" is «6 sagatdan soň» in Turkmen — an ablative
  // suffix plus a postposition — and composing that from a `{starts}` param is
  // exactly the grammar nobody here can read. A label is honest; an invented
  // sentence is not. Needs a native read before anyone writes one.
  "launcher.fixture_starts": "{teams} \u00B7\u00A0{starts}",
  "launcher.fixture_starts_wrapped": "{teams}\n{starts}",
  "time.minutes_short": "{n}\u00A0min",
  "time.hours_short": "{n}\u00A0sag",
  "launcher.fixture_score": "{teams} ·\u00A0{score}",
  // The WRAPPED form of the same line, chosen by measurement when the one-line
  // form will not fit the chip. It carries a newline where the one-line form
  // carries the separator: a "·" left leading the second line points at nothing
  // (owner, 2026-08-05). A full template per locale, never the one above with a
  // fragment swapped in — see the assembly rule at the top of this file.
  "launcher.fixture_score_wrapped": "{teams}\n{score}",
  // Derived from this catalog's own vocabulary, NOT translated from the Turkish —
  // an earlier pass put Turkish strings here and left a Turkmen user reading the
  // chip in one language and the panel header in another. `Göni goýumlar` is
  // panel.title verbatim (the chip names what it opens, as RU does); `Geçmek` is
  // cta.skip verbatim; `göni dalaşgär` is combo.legcount verbatim; `ähli` is
  // corpus-attested (lexicon/tk.md: «Bukmeker kompaniýalarynyň ählisi…»).
  // Turkmen does not inflect the noun after a numeral, so both plural forms match.
  "launcher.compact": "Göni goýumlar",
  "launcher.coupon_more": {
    one: "Ýene {n} göni dalaşgär",
    other: "Ýene {n} göni dalaşgär",
  },
  "peek.position": "{n}/{total}",
  "peek.all_picks": "Ähli goýumlar",
  "nps.question": "Maslahatlarymyz size näçe ýaraýar?",
  "nps.low": "Asla",
  "nps.high": "Örän",
  "nps.thanks": "Sag boluň!",
  "peek.skip": "Geçmek",
  "peek.prev": "Öňki goýum",
  "peek.next": "Indiki goýum",
  "peek.goto": "Goýum {n}",

  "panel.title": "Göni goýumlar",
  "panel.warming": "ýüklenýär…",
  "panel.finding_value": "Goýumlar gözlenýär…",
  "panel.collapse": "Ýygnamak",
  "panel.hide": "Gizle",
  "panel.subtitle": "{picks} · {when}",
  "panel.picks": { one: "{n} göni goýum", other: "{n} göni goýum" },
  "panel.updated_now": "häzir täzelendi",
  "panel.updated_min": { one: "{n} min öň", other: "{n} min öň" },
  "panel.updated_sec": { one: "{n} sek öň", other: "{n} sek öň" },

  "chip.live": "GÖNI",
  // P4: catalog vocabulary — duşuşyk (match, dative duşuşyga), goýum (bet). A
  // Turkmen noun stays singular after a numeral, so one form serves every count.
  "chip.bettors": "Bu duşuşyga {n} oýunçy goýum goýdy",
  "chip.live_count": "{n} GÖNI",
  "chip.tv": "Göni ýaýlym",
  "chip.tv_all": "Hemmesi ýaýlymda",
  "chip.multi_sport": "Dürli sport görnüşleri",

  "combo.selection": { one: "{n} duşuşykly ekspress", other: "{n} duşuşykly ekspress" },
  "combo.legcount": { one: "{n} göni dalaşgär", other: "{n} göni dalaşgär" },
  // `duşuşyk` (match) is this catalog's own leg noun — «{n} duşuşykly göni
  // ekspress» in the backend catalog. Turkmen does not inflect after a numeral.
  "combo.legcount_live": { one: "{n} göni duşuşyk", other: "{n} göni duşuşyk" },
  "combo.implied_approx": "ähtimallyk {pct}",
  "combo.tag_world_cup": "Dünýä Kubogy ekspressi",
  "combo.tag_focus": "{league} ekspressi",
  "combo.tag_live": "Göni ekspress",

  "card.vs": "{home} — {away}",

  "watch.eyebrow": "◆ Syn etmäge degýär",
  "watch.live_match": "Göni duşuşyk",
  "watch.suspended": "Goýumlar wagtlaýyn saklandy — häzir dalaşgäriň koeffisiýenti ýok.",

  "cta.add": "Goýum petegine goşmak",
  "cta.add_acca": "Ekspressi goşmak",
  "cta.adding": "Goşulýar…",
  "cta.sent": "✓ Petege goşuldy",
  "cta.sent_acca": "Goşuldy — petegi barlaň",
  "cta.in_slip": "✓ Petegiňizde",
  "cta.open_slip": "Petegi aç",
  "cta.opened": "Bukmekerde açyldy",
  // ⚠️ UNREVIEWED by a native reader — RU is the ship-gating locale (mobile design
  // spec §). Mirrors this catalog's own "cta.opened" phrasing rather than inventing one.
  "cta.add_failed": "Goşup bolmady — bukmekerde gözläň",
  // Owner native read 2026-09-01. Digitain serves no Turkmen at all, so there was
  // nothing to harvest — this comes from the owner, not from the operator.
  "cta.view_match": "oýny aç",
  "cta.skip": "başga bir maslahat",
  // ⛔ Chrome 2026-10-15: these fell back to English on a Turkmen card («ACCA»,
  // «3 events»). Lexicon-based (lexicon/tk.md), Codex prose review PASS.
  "combo.legcount_pre": { one: "{n} esasy dalaşgär", other: "{n} esasy dalaşgär" },
  "combo.legcount_pre_live": { one: "{n} duşuşyk", other: "{n} duşuşyk" },
  "combo.tag_pre": "Ekspress",
  "cta.place_bet": "Goýum goýmak",
  "cta.stake": "Goýum mukdary",
  "cta.cancel": "Ýatyrmak",
  "tier.low": "Pes töwekgelçilik",
  "tier.medium": "Orta töwekgelçilik",
  "tier.high": "Ýokary töwekgelçilik",
};

const CATALOGS: Record<Locale, Catalog> = { en: EN, fr: FR, ar: AR, ru: RU, tk: TK };

/** CLDR plural category for `n`. Arabic has six; English and French have two. */
function pluralRule(locale: Locale, n: number): Intl.LDMLPluralRule {
  try {
    return new Intl.PluralRules(locale).select(n);
  } catch {
    return n === 1 ? "one" : "other";
  }
}

export interface TParams {
  /** Drives plural selection when the message has plural forms. */
  n?: number;
  [key: string]: string | number | undefined;
}

/**
 * Resolve `key` in `locale`, interpolating `{name}` params.
 *
 * Every param value is bidi-isolated under RTL — params are the dynamic bits
 * (team names, scores, odds, counts), which are exactly the LTR runs that would
 * otherwise reorder inside an Arabic sentence.
 */
export function t(locale: Locale, key: string, params: TParams = {}): string {
  const msg = CATALOGS[locale]?.[key] ?? EN[key];
  if (msg == null) return key; // unknown key: show it rather than render blank

  let template: string;
  if (typeof msg === "string") {
    template = msg;
  } else {
    const n = typeof params.n === "number" ? params.n : 0;
    const rule = pluralRule(locale, n);
    template = msg[rule] ?? msg.other ?? Object.values(msg)[0] ?? key;
  }

  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const v = params[name];
    if (v == null) return whole;
    return ltr(v, locale);
  });
}

/** A `t()` bound to one locale — what the renderer actually carries around. */
export function translator(locale: Locale): (key: string, params?: TParams) => string {
  return (key, params) => t(locale, key, params);
}
